<script setup lang="ts">
// A weekly series drawn against its own history. This is Delta's headline form:
// the current season's line over a band spanning the baseline seasons' minimum
// to maximum, with the baseline mean dashed through it, all on one disease-week
// axis. The band is the shape of "normal" for that week — a reading above its
// top edge or below its bottom edge is the signal the chart exists to surface,
// which a bare line against a single mean would flatten.
//
// Null weeks are gaps, not zeroes: the backend omits `value` and the
// `baseline_*` figures for a week with no samples, and a chart that drew them as
// zero would invent a crash in the middle of a season.
import { computed } from "vue";
import { Line } from "vue-chartjs";
import {
	CategoryScale,
	Chart as ChartJS,
	Filler,
	Legend,
	LineController,
	LineElement,
	LinearScale,
	PointElement,
	Tooltip,
} from "chart.js";
import type { ChartData, ChartOptions, TooltipItem } from "chart.js";

import type { MapReviewSurveillanceWeek } from "@/type/api";

import {
	formatAmount,
	GRID_COLOR,
	legendOptions,
	OKABE_ITO,
	TICK_COLOR,
	TICK_FONT,
	withAlpha,
} from "./theme";

ChartJS.register(
	LineController,
	LineElement,
	PointElement,
	CategoryScale,
	LinearScale,
	Tooltip,
	Legend,
	Filler,
);

const props = defineProps<{
	/** The season's weekly series, disease weeks 1..53 of the report year. */
	weeks: MapReviewSurveillanceWeek[];
	/** The this-year line's name — a species, or "Total". */
	label: string;
	/** The baseline seasons' years, for the legend ("2021–2025"). */
	baselineYears: number[];
	/** The week axis' title, from the response ("Disease week (MMWR)"). */
	weekLabel: string;
	/** The canvas' accessible name. */
	ariaLabel: string;
	/** Draw without the legend, the axis title or hover points: a cell of a
	 * stack of small multiples, where six full legends would bury the lines. */
	compact?: boolean;
	/** The this-year line's colour, for a caller that tints the line by what it
	 * reads (a rising or falling slope). Defaults to the palette's first hue. */
	lineColor?: string;
}>();

// The season reads as one hue; the baseline reads as another. Colour is never
// the only cue — the legend names all three series and the mean is dashed — but
// keeping current distinct from history is what the eye should do first.
const LINE_COLOR = computed(() => props.lineColor ?? OKABE_ITO[0]);
const BASELINE_COLOR = OKABE_ITO[4];

/** "2021–2025" (an en dash), or "Baseline" when the server sent no years. */
const baselineName = computed(() => {
	const years = props.baselineYears;
	if (years.length === 0) return "Baseline";
	return `${years[0]}–${years[years.length - 1]}`;
});

const chartData = computed<ChartData<"line">>(() => ({
	labels: props.weeks.map((week) => String(week.week)),
	datasets: [
		{
			label: props.label,
			data: props.weeks.map((week) => week.value ?? null),
			borderColor: LINE_COLOR.value,
			backgroundColor: withAlpha(LINE_COLOR.value, 0.15),
			borderWidth: props.compact ? 1.5 : 2,
			tension: 0.3,
			pointRadius: props.compact ? 0 : 2,
			pointHoverRadius: props.compact ? 3 : 4,
			fill: false,
		},
		// The band's upper edge. Its fill drops to the lower edge at the next
		// index; no points and no stroke, so the band is the ink rather than its
		// boundary.
		{
			label: `${baselineName.value} range`,
			data: props.weeks.map((week) => week.baseline_max ?? null),
			borderColor: "transparent",
			backgroundColor: withAlpha(BASELINE_COLOR, 0.18),
			borderWidth: 0,
			pointRadius: 0,
			fill: "+1",
		},
		// The band's lower edge: the fill's floor and nothing else. Drawn as
		// nothing, and kept out of the legend.
		{
			label: `${baselineName.value} range floor`,
			data: props.weeks.map((week) => week.baseline_min ?? null),
			borderColor: "transparent",
			backgroundColor: "transparent",
			borderWidth: 0,
			pointRadius: 0,
			fill: false,
		},
		{
			label: `${baselineName.value} mean`,
			data: props.weeks.map((week) => week.baseline_mean ?? null),
			borderColor: BASELINE_COLOR,
			backgroundColor: "transparent",
			borderWidth: 1.5,
			borderDash: [4, 3],
			pointRadius: 0,
			pointHoverRadius: 3,
			fill: false,
		},
	],
}));

const chartOptions = computed<ChartOptions<"line">>(() => {
	const legend = legendOptions(true);
	return {
		responsive: true,
		maintainAspectRatio: false,
		// Index mode: hovering a week reports every series at that week, which is
		// the comparison the panel exists to make.
		interaction: { mode: "index", intersect: false },
		plugins: {
			// The lower band edge is an implementation detail: the legend names
			// the season, the baseline range and the baseline mean.
			legend: {
				...legend,
				display: !props.compact,
				labels: {
					...legend.labels,
					filter: (item) => item.datasetIndex !== 2,
				},
			},
			tooltip: {
				callbacks: {
					label: (context: TooltipItem<"line">) =>
						`${context.dataset.label ?? ""}: ${formatAmount(Number(context.parsed.y ?? 0))}`,
				},
			},
		},
		scales: {
			x: {
				grid: { display: false },
				title: {
					display: props.weekLabel !== "" && !props.compact,
					text: props.weekLabel,
					color: TICK_COLOR,
					font: TICK_FONT,
				},
				ticks: {
					color: TICK_COLOR,
					font: TICK_FONT,
					autoSkip: true,
					maxTicksLimit: props.compact ? 6 : 12,
					maxRotation: 0,
				},
			},
			y: {
				beginAtZero: true,
				grid: { color: GRID_COLOR },
				// Per-trap averages are fractional, so the axis carries decimals
				// where it needs them: formatAmount is the honesty of a measured
				// quantity, not a count.
				ticks: {
					color: TICK_COLOR,
					font: TICK_FONT,
					callback: (value: string | number) => formatAmount(Number(value)),
				},
			},
		},
	};
});
</script>

<template>
	<div class="chart-box" role="img" :aria-label="ariaLabel">
		<Line :data="chartData" :options="chartOptions" />
	</div>
</template>

<style scoped>
/* The panel owns the height; the box fills it, so a taller grid row grows the
   chart rather than leaving a gap under it. */
.chart-box {
	position: absolute;
	inset: 0;
}
</style>
